import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router';
import { object, string, boolean, ref } from 'yup';
import { useTranslation } from 'react-i18next';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { PATH_LOGIN } from '@jwp/ott-common/src/paths';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { cognitoSignUp, checkAccountExistence } from '@jwp/ott-common/src/services/integrations/CustomAccountService';
import Visibility from '@jwp/ott-theme/assets/icons/eye-on.svg?react';
import VisibilityOff from '@jwp/ott-theme/assets/icons/eye-off.svg?react';
import useToggle from '@jwp/ott-hooks-react/src/useToggle';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';

import { showToast } from '@jwp/ott-common/src/stores/ToastStore';

import AuthLayout from '../../containers/AuthLayout/AuthLayout';

import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import Button from '../../components/Button/Button';
import Icon from '../../components/Icon/Icon';
import { useAriaAnnouncer } from '../../containers/AnnouncementProvider/AnnoucementProvider';
import styles from '../SignInCognito/SignInCognito.module.scss';

// eslint-disable-next-line no-useless-escape
const FALLBACK_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type SignUpFormData = {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
  consent: boolean;
};

const SignUpCognito: React.FC = () => {
  const {
    t,
    i18n,
  } = useTranslation('account');
  const navigate = useNavigate();
  const { siteName, legalDocs, authConfig } = useConfigStore((s) => ({ 
      siteName: s.config.siteName,
      legalDocs: s.featureFlags?.common?.legal_docs,
      authConfig: s.featureFlags?.authentication,
  }));
  const { isShowLoginButton } = useAppConfig();
  const announce = useAriaAnnouncer();
  
  const [viewPassword, toggleViewPassword] = useToggle();
  const [viewConfirmPassword, toggleViewConfirmPassword] = useToggle();

  const currentLang = i18n.language.startsWith('es') ? 'es' : 'en';
  const privacyLink = legalDocs?.privacy_policy?.[currentLang] || legalDocs?.privacy_policy?.en || '';
  const termsLink = legalDocs?.terms_of_use?.[currentLang] || legalDocs?.terms_of_use?.en || '';

  const emailRegex = useMemo(() => {
    try {
      return authConfig?.email_regex ? new RegExp(authConfig.email_regex) : FALLBACK_EMAIL_REGEX;
    } catch (e) {
      console.error('Invalid email regex from config:', e);
      return FALLBACK_EMAIL_REGEX;
    }
  }, [authConfig?.email_regex]);

  const signUpSchema = useMemo(() => object().shape({
    firstName: string().trim().required(t('registration.first_name_required', 'First name is required')),
    lastName: string().trim().required(t('registration.last_name_required', 'Last name is required')),
    username: string()
      .trim()
      .required(t('registration.username_required', 'Username is required'))
      .min(6, t('registration.username_min_length', 'Username must be at least 6 characters'))
      .max(50, t('registration.username_max_length', 'Username must be at most 50 characters'))
      .matches(/^[\p{L}\p{M}\p{S}\p{N}\p{P}]+$/u, t('registration.username_no_spaces', 'Username cannot contain spaces')),
    email: string()
      .trim()
      .required(t('registration.email_required', 'Email is required'))
      .matches(emailRegex, t('registration.invalid_email', 'Invalid email'))
      .test(
        'max-domain-length',
        t('registration.email_domain_too_long', 'Email domain label too long'),
        (value) => {
          if (!value) return false;
          const domain = value.split('@')[1];
          if (!domain) return false;
          return domain.split('.').every((label) => label.length <= 63);
        },
      ),
    password: string()
      .trim()
      .required(t('registration.password_required', 'Password is required'))
      .min(8, t('registration.password_too_short', 'Password is too short'))
      .matches(/[A-Za-z]/, t('registration.password_one_letter', 'Password must contain at least one letter'))
      .matches(/\d/, t('registration.password_one_number', 'Password must contain at least one number'))
      .matches(/[%\-_!@#$*=&.]/, t('registration.password_special_character', 'Password must contain at least one special character')),
    confirmPassword: string()
      .trim()
      .oneOf([ref('password')], t('registration.confirm_password_mismatch', 'Passwords do not match. Please try again.'))
      .required(t('registration.confirm_password_required', 'Please confirm your password')),
    consent: boolean().oneOf([true], t('registration.consent_required', 'You must agree to the terms')).required(),
  }), [t, emailRegex]);

  const { values, errors, submitting, validationSchemaError, handleSubmit, handleChange } = useForm<SignUpFormData>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      username: '',
      password: '',
      confirmPassword: '',
      consent: false,
    },
    silentValidation: true,
    validationSchema: signUpSchema,

    onSubmit: async (formValues) => {
      if (emailExistsError || usernameExistsError) {
        throw new Error('');
      }
      const input = {
        uFirstName: formValues.firstName.trim(),
        uLastName: formValues.lastName.trim(),
        uEmailId: formValues.email.trim(),
        uUserName: formValues.username.trim(),
        password: formValues.password.trim(),
      };

      await cognitoSignUp(input);
    },
    onSubmitSuccess: () => {
      showToast(t('registration.sign_up_success', 'Account created successfully! Please sign in.'), 'success');
      navigate(PATH_LOGIN);
    },
    onSubmitError: ({ error }) => {
      console.error('Sign up error:', error);
      let errorMessage = t('login.unexpected_error');
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      showToast(errorMessage, 'error');
    },
  });


  const [emailExistsError, setEmailExistsError] = useState<string | null>(null);
  const [usernameExistsError, setUsernameExistsError] = useState<string | null>(null);

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (!value.trim()) return;

    if (name === 'email') {
      const exists = await checkAccountExistence(value.trim(), 'email');
      setEmailExistsError(exists ? t('registration.email_already_registered') : null);
    } else if (name === 'username') {
      const exists = await checkAccountExistence(value.trim(), 'username');
      setUsernameExistsError(exists ? t('registration.username_already_taken') : null);
    }
  };


  return (
    <AuthLayout 
      title={t('registration.sign_up', 'Sign Up')} 
      subtitle={t('registration.sign_up_subtitle', 'Create your play account')}
    >
      <Helmet>
        <title>
          {t('registration.sign_up', 'Sign Up')} | {siteName}
        </title>
      </Helmet>

      <div className={styles.overlayWrap}>
        {submitting && (
          <div className={styles.loaderLayer}>
            <LoadingOverlay inline transparentBackground />
          </div>
        )}
        
        <div className={styles.formContainer}>
          <form onSubmit={handleSubmit} noValidate>
            <div className={styles.formBody}>
              <div className={styles.dualFields}>
                <div className={styles.field}>
                  <label htmlFor="firstName" className={styles.fieldLabel}>
                    {t('registration.first_name', 'First Name')}
                    <span className={styles.required} aria-hidden="true">*</span>
                  </label>
                  <div className={styles.inputWrap}>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      className={styles.input}
                      value={values.firstName}
                      onChange={handleChange}
                      placeholder={t('registration.first_name_placeholder', 'Enter first name')}
                      required
                    />
                  </div>
                  {errors.firstName && <p className={styles.helperError}>{errors.firstName}</p>}
                </div>

                <div className={styles.field}>
                  <label htmlFor="lastName" className={styles.fieldLabel}>
                    {t('registration.last_name', 'Last Name')}
                    <span className={styles.required} aria-hidden="true">*</span>
                  </label>
                  <div className={styles.inputWrap}>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      className={styles.input}
                      value={values.lastName}
                      onChange={handleChange}
                      placeholder={t('registration.last_name_placeholder', 'Enter last name')}
                      required
                    />
                  </div>
                  {errors.lastName && <p className={styles.helperError}>{errors.lastName}</p>}
                </div>
              </div>

              <div className={styles.field}>
                <label htmlFor="email" className={styles.fieldLabel}>
                  {t('registration.email', 'Email ID')}
                  <span className={styles.required} aria-hidden="true">*</span>
                </label>
                <div className={styles.inputWrap}>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className={styles.input}
                    value={values.email}
                    onChange={(e) => {
                      handleChange(e);
                      setEmailExistsError(null);
                    }}
                    onBlur={handleBlur}
                    placeholder={t('registration.email_placeholder', 'Enter email id')}
                    required
                  />
                </div>
                {(errors.email || emailExistsError) && <p className={styles.helperError}>{errors.email || emailExistsError}</p>}
              </div>

              <div className={styles.field}>
                <label htmlFor="username" className={styles.fieldLabel}>
                  {t('registration.username', 'Username')}
                  <span className={styles.required} aria-hidden="true">*</span>
                </label>
                <div className={styles.inputWrap}>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    className={styles.input}
                    value={values.username}
                    onChange={(e) => {
                      handleChange(e);
                      setUsernameExistsError(null);
                    }}
                    onBlur={handleBlur}
                    placeholder={t('registration.username_placeholder', 'Enter username')}
                    required
                  />
                </div>
                {(errors.username || usernameExistsError) && <p className={styles.helperError}>{errors.username || usernameExistsError}</p>}
              </div>

              <div className={styles.field}>
                <label htmlFor="password" className={styles.fieldLabel}>
                  {t('registration.password', 'Password')}
                  <span className={styles.required} aria-hidden="true">*</span>
                </label>
                <div className={`${styles.inputWrap} ${styles.inputWithToggle}`}>
                  <input
                    id="password"
                    name="password"
                    type={viewPassword ? 'text' : 'password'}
                    className={styles.input}
                    value={values.password}
                    onChange={handleChange}
                    placeholder={t('registration.password_placeholder', 'Enter password')}
                    required
                  />
                  <button
                    type="button"
                    className={styles.toggleVisibility}
                    onClick={() => toggleViewPassword()}
                    aria-label={t('login.view_password')}
                  >
                    <Icon icon={viewPassword ? Visibility : VisibilityOff} />
                  </button>
                </div>
                {errors.password && <p className={styles.helperError}>{errors.password}</p>}
              </div>

              <div className={styles.field}>
                <label htmlFor="confirmPassword" className={styles.fieldLabel}>
                  {t('registration.confirm_password', 'Confirm Password')}
                  <span className={styles.required} aria-hidden="true">*</span>
                </label>
                <div className={`${styles.inputWrap} ${styles.inputWithToggle}`}>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={viewConfirmPassword ? 'text' : 'confirmPassword'}
                    className={styles.input}
                    value={values.confirmPassword}
                    onChange={handleChange}
                    placeholder={t('registration.confirm_password_placeholder', 'Retype password')}
                    required
                  />
                  <button
                    type="button"
                    className={styles.toggleVisibility}
                    onClick={() => toggleViewConfirmPassword()}
                    aria-label={t('login.view_password')}
                  >
                    <Icon icon={viewConfirmPassword ? Visibility : VisibilityOff} />
                  </button>
                </div>
                {errors.confirmPassword && <p className={styles.helperError}>{errors.confirmPassword}</p>}
              </div>

              <div className={styles.checkboxContainer}>
                <input 
                  type="checkbox" 
                  id="consent" 
                  name="consent"
                  className={styles.checkboxInput}
                  checked={values.consent}
                  onChange={handleChange}
                />
                <label htmlFor="consent" className={styles.checkboxLabel}>
                  Agree to the <a href={termsLink} target="_blank" rel="noopener noreferrer">Terms of Use</a> and <a href={privacyLink} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                </label>
              </div>
            </div>

            <Button
              type="submit"
              color='primary'
              className={styles.submit}
              busy={submitting}
              disabled={submitting || !values.consent}
              label={submitting ? t('registration.signing_up', 'Signing Up...') : t('registration.sign_up', 'Sign Up')}
              fullWidth
            />
            {isShowLoginButton && (
              <p className={styles.footer}>
                {t('registration.already_have_account', 'Already have an account?')}
                <button type="button" className={styles.signUpLink} onClick={() => navigate(PATH_LOGIN)}>
                  {t('login.sign_in', 'Sign In')}
                </button>
              </p>
            )}
          </form>
        </div>
      </div>
    </AuthLayout>
  );
};

export default SignUpCognito;
